import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trackClick } from "@/components/_helpers";
import { getDeviceInfo } from "@/lib/analyticsClient";
import { cleanNickname } from "@/utils/arcade/leaderboard";
import { decideStart, freeRunsLeft, readFreeRunsUsed, recordFreeRun } from "@/utils/arcade/playGate";
import { BADGES, readProgress, recordChallengeRank, recordRunEnd, recordRunStart, streakFrom, writeProgress, PROGRESS_CHANGED_EVENT, type ArcadeProgress, type BadgeId } from "@/utils/arcade/progress";
import { dropHeldScore, holdScore, readHeldScores, readNickname, writeNickname, type HeldScore } from "@/utils/arcade/scores";
import { utcDateKey } from "@/utils/arcade/dailyChallenge";
import type { ArcadeGameKind } from "@/utils/arcade/games";
import { getArcadeBackend, type SubmitResult } from "./arcadeBackend";
import { useArcadeAccess, type ArcadeAccessState } from "./useArcadeAccess";

export interface RunResult {
  score: number;
  level: number;
  durationSec: number;
  bossBeaten: boolean;
}

export type GateWhy = "free-runs-used" | "members-only" | "save-score";

export type PostState =
  | { status: "idle" }
  | { status: "posting" }
  | { status: "posted"; result: Extract<SubmitResult, { posted: true }> }
  | { status: "held" }
  // Posted, but big enough that it waits for a check before it shows.
  | { status: "review"; message: string }
  | { status: "error"; message: string };

export interface ArcadeRetentionOptions {
  game: ArcadeGameKind;
  songSlug: string;
  songTitle: string;
  // The daily challenge date when this page was opened from the challenge.
  challenge?: string | null;
  // Endless run: members only from the first run.
  membersOnly?: boolean;
}

export interface ArcadeRetention extends ArcadeRetentionOptions {
  access: ArcadeAccessState;
  challenge: string | null;
  freeLeft: number | null;
  progress: ArcadeProgress;
  streak: number;
  nickname: string;
  lastRun: RunResult | null;
  postState: PostState;
  badgeToast: BadgeId[];
  // resumable: the dialog stopped a Start press, so "Play now" can finish it.
  dialog: { open: boolean; why: GateWhy; resumable: boolean };
  requestStart: (start: () => void) => boolean;
  runStarted: () => void;
  runEnded: (result: RunResult) => void;
  post: (nickname: string) => Promise<void>;
  openDialog: (why: GateWhy, resumable?: boolean) => void;
  closeDialog: () => void;
  unlockedByEmail: () => void;
  playNow: () => void;
  clearBadgeToast: () => void;
}

const track = (target: string, label: string, extra: { track?: string; value?: number } = {}) => {
  if (getDeviceInfo().isBot) return;
  trackClick(target, { label, section: "arcade", ...extra });
};

export function useArcadeRetention(options: ArcadeRetentionOptions): ArcadeRetention {
  const { game, songSlug, songTitle, membersOnly = false } = options;
  const challenge = options.challenge ?? null;
  const access = useArcadeAccess();
  const [used, setUsed] = useState(() => readFreeRunsUsed());
  const [progress, setProgress] = useState<ArcadeProgress>(() => readProgress());
  const [nickname, setNickname] = useState(() => readNickname());
  const [lastRun, setLastRun] = useState<RunResult | null>(null);
  const [postState, setPostState] = useState<PostState>({ status: "idle" });
  const [badgeToast, setBadgeToast] = useState<BadgeId[]>([]);
  const [dialog, setDialog] = useState<{ open: boolean; why: GateWhy; resumable: boolean }>({ open: false, why: "free-runs-used", resumable: false });
  const pendingStartRef = useRef<(() => void) | null>(null);
  const pendingPostRef = useRef<{ run: RunResult; nickname: string; runToken: string | null } | null>(null);
  // The server's receipt for the run in progress, then for the last run.
  const runTokenRef = useRef<Promise<string | null> | null>(null);
  const lastRunTokenRef = useRef<Promise<string | null> | null>(null);
  const gateShownRef = useRef(false);
  const accessRef = useRef(access);
  accessRef.current = access;

  useEffect(() => {
    const sync = () => setProgress(readProgress());
    window.addEventListener(PROGRESS_CHANGED_EVENT, sync);
    return () => window.removeEventListener(PROGRESS_CHANGED_EVENT, sync);
  }, []);

  const announce = useCallback((earned: BadgeId[]) => {
    if (earned.length === 0) return;
    setBadgeToast(earned);
    for (const id of earned) track(`arcade:badge:${id}`, `Arcade badge ${BADGES[id].name}`);
  }, []);

  const openDialog = useCallback(
    (why: GateWhy, resumable = false) => {
      setDialog({ open: true, why, resumable });
      gateShownRef.current = true;
      track("arcade:gate-shown", `Arcade gate shown (${why})`, { track: songTitle });
    },
    [songTitle],
  );

  const closeDialog = useCallback(() => {
    setDialog((current) => ({ ...current, open: false }));
    pendingStartRef.current = null;
  }, []);

  const requestStart = useCallback(
    (start: () => void) => {
      const decision = decideStart({ access: accessRef.current, used: readFreeRunsUsed(), membersOnly });
      if (decision.kind === "gate") {
        pendingStartRef.current = start;
        openDialog(decision.why, true);
        return false;
      }
      if (decision.counted) {
        setUsed(recordFreeRun());
        track("arcade:free-run", `Arcade free play used, ${decision.left} left`, { track: songTitle, value: decision.left });
      }
      return true;
    },
    [membersOnly, openDialog, songTitle],
  );

  const runStarted = useCallback(() => {
    const now = Date.now();
    // Ask for the receipt now; the score can only be posted with it.
    runTokenRef.current = getArcadeBackend()
      .startRun({ songSlug, game })
      .catch(() => null);
    const next = recordRunStart(readProgress(), game, now);
    writeProgress(next.progress);
    setProgress(next.progress);
    // Last run's badges are old news; this run's show at its game over.
    setBadgeToast([]);
    announce(next.earned);
    setPostState({ status: "idle" });
    setLastRun(null);
    if (challenge) track("arcade:challenge-start", `Daily challenge start ${challenge}`, { track: songTitle });
  }, [announce, challenge, game, songSlug, songTitle]);

  const runEnded = useCallback(
    (result: RunResult) => {
      lastRunTokenRef.current = runTokenRef.current;
      setLastRun(result);
      const next = recordRunEnd(readProgress(), result, Date.now());
      writeProgress(next.progress);
      setProgress(next.progress);
      announce(next.earned);
    },
    [announce],
  );

  const held = useCallback(
    (run: RunResult, runToken: string | null): HeldScore => ({
      songSlug,
      game,
      score: run.score,
      level: run.level,
      durationSec: run.durationSec,
      challenge: challenge ?? undefined,
      runToken: runToken ?? undefined,
      at: Date.now(),
    }),
    [challenge, game, songSlug],
  );

  const submit = useCallback(
    async (entry: HeldScore, name: string): Promise<SubmitResult> => {
      const current = accessRef.current;
      return getArcadeBackend().submitScore({
        songSlug: entry.songSlug,
        game: entry.game,
        score: entry.score,
        level: entry.level,
        durationSec: entry.durationSec,
        nickname: name,
        challenge: entry.challenge,
        runToken: entry.runToken,
        email: current.reason === "member" || current.reason === "admin" ? undefined : current.storedEmail ?? undefined,
        unlockToken: current.reason === "member" || current.reason === "admin" ? undefined : current.unlockToken ?? undefined,
      });
    },
    [],
  );

  const post = useCallback(
    async (rawName: string) => {
      if (!lastRun) return;
      const name = cleanNickname(rawName);
      if (name.ok === false) {
        setPostState({ status: "error", message: name.reason });
        return;
      }
      writeNickname(name.nickname);
      setNickname(name.nickname);
      const runToken = (await lastRunTokenRef.current) ?? null;
      const current = accessRef.current;
      if (!current.canPostScores) {
        if (current.unlocked && current.reason === "email") {
          holdScore(held(lastRun, runToken));
          setPostState({ status: "held" });
          track("arcade:score-held", "Arcade score held for confirmation", { track: songTitle, value: lastRun.score });
          return;
        }
        pendingPostRef.current = { run: lastRun, nickname: name.nickname, runToken };
        openDialog("save-score");
        return;
      }
      setPostState({ status: "posting" });
      try {
        const result = await submit(held(lastRun, runToken), name.nickname);
        if (result.posted === false) {
          if (result.review) {
            setPostState({ status: "review", message: result.reason });
            track("arcade:score-review", "Arcade score sent for review", { track: songTitle, value: lastRun.score });
          } else setPostState({ status: "error", message: result.reason });
          return;
        }
        setPostState({ status: "posted", result });
        track("arcade:score-posted", `Arcade score posted, rank ${result.rank}`, { track: songTitle, value: lastRun.score });
        if (result.challengeRank !== null) {
          const next = recordChallengeRank(readProgress(), result.challengeRank, Date.now());
          writeProgress(next.progress);
          setProgress(next.progress);
          announce(next.earned);
        }
      } catch {
        setPostState({ status: "error", message: "The leaderboard did not answer. Try again in a moment." });
      }
    },
    [announce, held, lastRun, openDialog, songTitle, submit],
  );

  // After an email signup from the dialog: play on, and keep the score that
  // asked for the signup until the email is confirmed.
  const unlockedByEmail = useCallback(
    () => {
      track("arcade:gate-unlocked-email", "Arcade unlocked by email", { track: songTitle });
      const pending = pendingPostRef.current;
      if (pending) {
        holdScore(held(pending.run, pending.runToken));
        pendingPostRef.current = null;
        setPostState({ status: "held" });
      }
    },
    [held, songTitle],
  );

  const playNow = useCallback(() => {
    const start = pendingStartRef.current;
    setDialog((current) => ({ ...current, open: false }));
    pendingStartRef.current = null;
    start?.();
  }, []);

  // Signed in from the dialog: close it, count the unlock and post the score
  // that was waiting, if any.
  useEffect(() => {
    if (access.reason !== "member" && access.reason !== "admin") return;
    if (gateShownRef.current) {
      gateShownRef.current = false;
      track("arcade:gate-unlocked-signin", "Arcade unlocked by sign in", { track: songTitle });
      setDialog((current) => ({ ...current, open: false }));
    }
    const pending = pendingPostRef.current;
    if (pending && access.canPostScores) {
      pendingPostRef.current = null;
      void post(pending.nickname);
    }
  }, [access.reason, access.canPostScores, post, songTitle]);

  // Scores saved before the email was confirmed go up the first time the
  // arcade opens with proof on this device.
  const flushingRef = useRef(false);
  useEffect(() => {
    if (!access.canPostScores || flushingRef.current) return;
    const name = readNickname();
    const waiting = readHeldScores();
    if (!name || waiting.length === 0) return;
    flushingRef.current = true;
    void (async () => {
      for (const entry of waiting) {
        try {
          const result = await submit(entry, name);
          dropHeldScore(entry);
          if (result.posted) track("arcade:score-posted", `Arcade held score posted, rank ${result.rank}`, { value: entry.score });
          if (result.posted && entry.songSlug === songSlug && entry.game === game) setPostState({ status: "posted", result });
        } catch {
          break;
        }
      }
      flushingRef.current = false;
    })();
  }, [access.canPostScores, game, songSlug, submit]);

  const clearBadgeToast = useCallback(() => setBadgeToast([]), []);
  const today = utcDateKey(Date.now());
  const streak = useMemo(() => streakFrom(progress.days, today), [progress.days, today]);
  const freeLeft = access.unlocked || access.resolving ? null : membersOnly ? 0 : freeRunsLeft(used);

  return {
    game,
    songSlug,
    songTitle,
    membersOnly,
    challenge,
    access,
    freeLeft,
    progress,
    streak,
    nickname,
    lastRun,
    postState,
    badgeToast,
    dialog,
    requestStart,
    runStarted,
    runEnded,
    post,
    openDialog,
    closeDialog,
    unlockedByEmail,
    playNow,
    clearBadgeToast,
  };
}
